/**
 * Utility to check if a user has access to a specific route
 * based on their role, subscription features, and role permissions.
 *
 * @param {Object} route - The route configuration object from routes.js
 * @param {string} userRole - The current user's role (e.g., 'SUPER_ADMIN', 'Company', 'Employee')
 * @param {Array} features - The active subscription features array
 * @param {Array} permissions - The employee's permissions array (only checked if role is Employee)
 * @returns {boolean} - True if access is permitted, false otherwise
 */
export const checkRouteAccess = (route, userRole, features = [], permissions = []) => {
  if (!route) return false;

  // 1. Guard against accessing layout outside of authorized role boundaries
  if (route.layout === "/admin" && userRole !== "SUPER_ADMIN") {
    return false;
  }
  if (route.layout === "/company" && userRole === "SUPER_ADMIN") {
    return false;
  }

  // 2. Enforce explicit allowed roles if configured
  if (route.allowedRoles && !route.allowedRoles.includes(userRole)) {
    return false;
  }

  // 3. Feature-based subscription check for company/tenant level modules
  if (route.layout === "/company" && route.feature) {
    const featureSetting = features?.find((f) => f.feature_key === route.feature);
    if (!featureSetting || featureSetting.is_enabled !== 1) {
      return false;
    }
  }

  // 4. Fine-grained RBAC permission check for employees
  // Company user represents the tenant owner and has complete administrative permissions by default.
  if (
    route.layout === "/company" &&
    route.permissionModule &&
    userRole?.toLowerCase() === "employee"
  ) {
    const hasModulePermission = permissions?.some((p) =>
      p.startsWith(route.permissionModule)
    );
    if (!hasModulePermission) {
      return false;
    }
  }

  return true;
};
